import { useState, useEffect, useRef } from 'react';

// ==================== ID生成・DB送信機能 ====================
// 2026-07-16 リードDB統一: 旧URL(AKfycbz1…)は403でCV全件ロスト状態だった。
// gas-questionnaire経由でAIシート「LP完結リード」へ集約（lp02と同じ経路）
const GAS_ENDPOINT = "https://script.google.com/macros/s/AKfycbwMIUr0pXSiubpQ4w7mYoO8WE9RTanN2b1ZAcwPiqky0dh4K7oPvivWlo8asOBPuJPi/exec";
const FORM_FIELDS = ["qualification", "birth_year", "birth_month", "birth_day", "name", "phone", "postal_code", "prefecture", "work_style", "transfer_timing"];

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateVisitorId(): string {
  const key = 'lp_common_visitor_id';
  let vid = localStorage.getItem(key);
  if (!vid) {
    vid = `vis_${generateUUID()}`;
    localStorage.setItem(key, vid);
  }
  return vid;
}

function generateConversionId(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const randomStr = Math.random().toString(36).substring(2, 10);
  return `cvid_${dateStr}_${randomStr}`;
}

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
  };
}

function getAffiliateParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    aff_company: params.get('aff_company') || '',
    aff_id: params.get('aff_id') || '',
    click_id: params.get('click_id') || '',
  };
}

function getLpId(): string {
  const url = window.location.origin + window.location.pathname;
  return url.endsWith('/') ? url : url + '/';
}

function normalizePhone(phone: string): string {
  return phone.replace(/-/g, '');
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function sendGA4Event(eventName: string) {
  if (typeof window.gtag === 'undefined') return;
  const sessionKey = `ga4_event_${eventName}`;
  if (sessionStorage.getItem(sessionKey)) return;
  window.gtag('event', eventName);
  sessionStorage.setItem(sessionKey, 'sent');
}

// ==================== Scroll Depth Tracking ====================
function useScrollDepth() {
  useEffect(() => {
    const thresholds = [10, 50, 80];
    const fired = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (docHeight <= 0) return;
      const percent = Math.round((scrollTop / docHeight) * 100);

      thresholds.forEach((t) => {
        if (!fired.has(t) && percent >= t) {
          fired.add(t);
          sendGA4Event(`nijikango_scroll_${t}`);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
}

// ==================== FloatingParticle ====================
const FloatingParticle = ({ delay, top }: { delay: number; top: string }) => (
  <div style={{
    position: 'absolute',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,182,193,0.2), rgba(255,192,203,0.1))',
    filter: 'blur(15px)',
    top,
    left: '50%',
    transform: 'translateX(-50%)',
    animation: 'floatUp 8s ease-in-out infinite',
    animationDelay: `${delay}s`,
    pointerEvents: 'none',
  }} />
);

// ==================== JobCard ====================
interface JobData {
  area: string;
  type: string;
  salary: string;
  overtime: string;
  description: string;
  tags: string[];
  accentColor: string;
  isNew?: boolean;
}

const JobCard = ({ job }: { job: JobData }) => (
  <div
    style={{
      background: '#ffffff',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(58,45,61,0.10)',
      width: '100%',
      cursor: 'default',
    }}
  >
    <div style={{ background: `${job.accentColor}22`, padding: '16px', position: 'relative' }}>
      {job.isNew && (
        <span style={{
          display: 'inline-block',
          background: job.accentColor,
          color: '#fff',
          fontSize: '10px',
          fontWeight: '700',
          padding: '3px 10px',
          borderRadius: '50px',
          marginBottom: '6px',
        }}>NEW</span>
      )}
      <div style={{ fontSize: '12px', color: '#8a7a8e', marginBottom: '4px' }}>{job.area}</div>
      <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#3a2d3d', fontFamily: "'Zen Maru Gothic', sans-serif", lineHeight: '1.4' }}>{job.type}</h3>
    </div>
    <div style={{ padding: '16px 16px 20px' }}>
      <div style={{ fontSize: '26px', fontWeight: '700', color: '#FF69B4', fontFamily: "'Zen Maru Gothic', sans-serif", marginBottom: '12px' }}>{job.salary}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '13px', color: '#6b5a6e' }}>
        <span>⏱</span><span>月残業時間: {job.overtime}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '12px', fontSize: '13px', color: '#6b5a6e' }}>
        <span>📋</span><span>{job.description}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {job.tags.map((tag, idx) => (
          <span key={idx} style={{ fontSize: '11px', color: '#FF69B4', border: '1.5px solid #FFB6C1', padding: '4px 10px', borderRadius: '50px', fontWeight: '500' }}>{tag}</span>
        ))}
      </div>
    </div>
  </div>
);

// ==================== 求人データ ====================
const jobsDataBase: JobData[] = [
  { area: '東京都 中央区', type: 'クリニック（マネジメント業務）', salary: '58万円〜', overtime: '記載なし', description: 'マネジメント業務', tags: ['賞与実績あり', '年間休日120日'], accentColor: '#FFB6C1', isNew: true },
  { area: '東京都', type: '美容クリニック', salary: '50万円〜', overtime: '記載なし', description: '美容クリニック看護業務', tags: ['残業なし', '土日祝休み', '月給50万円以上可', '年間休日120日'], accentColor: '#DDA0DD', isNew: true },
  { area: '埼玉県', type: 'クリニック', salary: '52万円〜', overtime: '記載なし', description: 'クリニック看護業務', tags: ['土日祝休み', '年間休日120日'], accentColor: '#B8E0D2', isNew: true },
  { area: '東京都 立川市', type: 'クリニック', salary: '42万円〜', overtime: '記載なし', description: 'デンタルクリニック看護業務', tags: ['賞与要確認', '年間休日120日'], accentColor: '#B0E0E6', isNew: true },
  { area: '東京都 渋谷区', type: 'クリニック', salary: '41.7万円〜', overtime: '記載なし', description: 'クリニック看護業務', tags: ['賞与1.2ヶ月分', '年間休日126日'], accentColor: '#FFD1DC', isNew: true },
  { area: '東京都 中央区', type: '男性専門美容クリニック', salary: '33〜50万円', overtime: '月平均15時間', description: 'オペ介助・医療脱毛・美容皮膚科施術・術後ケア', tags: ['賞与年2回', '年間休日120日', 'オープニングスタッフ'], accentColor: '#FFB6C1', isNew: true },
  { area: '全国多数拠点あり', type: '美容外科クリニック', salary: '40.9万円〜', overtime: '記載なし', description: '施術補助・医療脱毛・美容点滴・患者様対応', tags: ['年間休日120日以上', '美容未経験OK', 'インセンティブあり', '社割あり', '研修充実'], accentColor: '#FFD1DC', isNew: true },
  { area: '東京都 千代田区', type: '美容皮膚科クリニック', salary: '35〜50万円', overtime: '月平均5時間', description: 'ピコトーニング・フォトフェイシャル・ダーマペン・医療脱毛・ハイフ等', tags: ['年間休日116日', '残業月5時間', 'ブランク可', '第二新卒可', '有給取得率80%'], accentColor: '#B8E0D2', isNew: true },
];

// 月収降順ソート（"83万円〜" などの文字列から数値を抽出）
function parseSalary(salary: string): number {
  const match = salary.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : 0;
}

function sortBySalaryDesc(arr: JobData[]): JobData[] {
  return [...arr].sort((a, b) => parseSalary(b.salary) - parseSalary(a.salary));
}

const prefectures = [
  '東京都', '神奈川県', '埼玉県', '千葉県', '茨城県', '栃木県', '群馬県',
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

export default function LandingPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({ qualification: '', workStyle: '', postalCode: '', name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visitorId, setVisitorId] = useState('');
  const [shuffledJobs, setShuffledJobs] = useState<JobData[]>([]);

  useScrollDepth();

  const touchStartXRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number | null>(null);
  const autoSlideRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setVisitorId(getOrCreateVisitorId());
    setShuffledJobs(sortBySalaryDesc(jobsDataBase));
  }, []);

  const TOTAL = shuffledJobs.length || jobsDataBase.length;

  const startAutoSlide = () => {
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    autoSlideRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev >= TOTAL - 1 ? 0 : prev + 1));
    }, 3000);
  };

  useEffect(() => {
    if (shuffledJobs.length === 0) return;
    startAutoSlide();
    return () => { if (autoSlideRef.current) clearInterval(autoSlideRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shuffledJobs.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    startAutoSlide();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const diffX = Math.abs(e.touches[0].clientX - touchStartXRef.current);
    if (diffX > 10) e.stopPropagation();
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const diff = touchStartXRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      setCurrentSlide((prev) => diff > 0
        ? (prev >= TOTAL - 1 ? 0 : prev + 1)
        : (prev <= 0 ? TOTAL - 1 : prev - 1));
    }
    touchStartXRef.current = null;
    startAutoSlide();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDraggingRef.current || dragStartXRef.current === null) return;
    const diff = dragStartXRef.current - e.clientX;
    if (Math.abs(diff) > 40) {
      setCurrentSlide((prev) => diff > 0
        ? (prev >= TOTAL - 1 ? 0 : prev + 1)
        : (prev <= 0 ? TOTAL - 1 : prev - 1));
    }
    isDraggingRef.current = false;
    dragStartXRef.current = null;
    startAutoSlide();
  };
  const handleMouseLeave = () => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      dragStartXRef.current = null;
      startAutoSlide();
    }
  };

  const openForm = (gaEvent?: string) => {
    setIsFormOpen(true);
    document.body.style.overflow = 'hidden';
    sendGA4Event('nijikango_click_1');
    if (gaEvent) sendGA4Event(gaEvent);
  };
  const closeForm = () => {
    setIsFormOpen(false);
    document.body.style.overflow = 'auto';
  };

  const handleSubmit = async () => {
    if (!formData.qualification || !formData.workStyle || !formData.postalCode || !formData.name || !formData.phone) {
      alert('すべての項目を入力してください。');
      return;
    }
    const normalizedPhone = normalizePhone(formData.phone);
    if (normalizedPhone.length !== 11 || !/^\d{11}$/.test(normalizedPhone)) {
      alert('電話番号は11桁の数字で入力してください（例：09012345678）。');
      return;
    }
    const postalCodeClean = formData.postalCode.replace(/-/g, '');
    if (!/^\d{7}$/.test(postalCodeClean)) {
      alert('郵便番号は7桁の数字で入力してください（例：1234567）。');
      return;
    }
    setIsSubmitting(true);
    try {
      const conversionId = generateConversionId();
      const utmParams = getUTMParams();
      const affParams = getAffiliateParams();
      const lpId = getLpId();
      const personalInfo: Record<string, string> = {
        qualification: '', birth_year: '', birth_month: '', birth_day: '',
        name: '', phone: '', postal_code: '', prefecture: '', work_style: '', transfer_timing: '',
      };
      if (FORM_FIELDS.includes('qualification')) personalInfo.qualification = formData.qualification;
      if (FORM_FIELDS.includes('work_style')) personalInfo.work_style = formData.workStyle;
      personalInfo.postal_code = postalCodeClean;
      if (FORM_FIELDS.includes('name')) personalInfo.name = formData.name;
      if (FORM_FIELDS.includes('phone')) personalInfo.phone = normalizedPhone;
      const submitData = {
        visitor_id: visitorId,
        conversion_id: conversionId,
        lp_id: lpId,
        ...personalInfo,
        ...utmParams,
        ...affParams,
        referrer: document.referrer || '',
        landing_page: window.location.href,
        submitted_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
      };
      await fetch(GAS_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });
      sendGA4Event('generate_lead');
      alert('送信が完了しました！担当者より24時間以内にご連絡いたします。');
      setFormData({ qualification: '', workStyle: '', postalCode: '', name: '', phone: '' });
      closeForm();
    } catch {
      alert('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Zen+Maru+Gothic:wght@400;500;700&family=Noto+Sans+JP:wght@300;400;500;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Noto Sans JP', sans-serif; color: #3a2d3d; background: #fefbfc; overflow-x: hidden; }
    @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes floatUp { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-30px); } }
    @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
    @keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.02); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
  `;

  const hl: React.CSSProperties = { color: '#FF69B4' };

  const ctaBtnBase: React.CSSProperties = {
    background: 'linear-gradient(135deg, #FF69B4 0%, #FFB6C1 100%)',
    backgroundSize: '200% auto',
    color: '#fff',
    border: 'none',
    borderRadius: '50px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(255,105,180,0.30)',
    whiteSpace: 'nowrap' as const,
    fontFamily: "'Noto Sans JP', sans-serif",
  };

  return (
    <>
      <style>{globalStyles}</style>

      {/* ===== HEADER ===== */}
      <header style={{
        background: '#ffffff',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 12px rgba(58,45,61,0.06)',
      }}>
        <a href="https://nijikango.co.jp/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img
            src="https://static.readdy.ai/image/2eeb7004bec3dab6605078c5e30f883b/03d84a07dc69ca97a2487f53039402b6.jpeg"
            alt="ニジ看護転職"
            style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
          />
        </a>
        <span style={{ fontSize: '10px', color: '#8a7a8e', letterSpacing: '0.3px', lineHeight: '1.6', textAlign: 'right' }}>
          厚生労働大臣許可番号<br />13-ユ-317878
        </span>
      </header>

      {/* ===== HERO ===== */}
      <section style={{
        minHeight: '62vh',
        backgroundImage: 'url(https://static.readdy.ai/image/2eeb7004bec3dab6605078c5e30f883b/ceb96b9268cc6fdb4e531960ae700049.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '44px 20px 52px',
        overflow: 'hidden',
      }}>
        {/* 左側を少し白くして文字を読みやすく、右側は透明に近い */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(105deg, rgba(255,248,250,0.78) 0%, rgba(255,245,248,0.52) 45%, rgba(255,240,245,0.18) 100%)',
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
        }} />
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,182,193,0.20), transparent)', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-100px', left: '-100px', width: '320px', height: '320px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,192,203,0.15), transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <FloatingParticle delay={0} top="15%" />
        <FloatingParticle delay={1.5} top="55%" />
        <FloatingParticle delay={3} top="80%" />

        <div style={{ maxWidth: '100%', textAlign: 'center', position: 'relative', zIndex: 10, animation: 'fadeInUp 0.9s ease-out' }}>
          <h1 style={{
            fontSize: '25px',
            fontWeight: '700',
            lineHeight: '1.55',
            marginBottom: '18px',
            fontFamily: "'Zen Maru Gothic', sans-serif",
            color: '#1e1020',
            textShadow: '0 1px 8px rgba(255,255,255,0.9), 0 0px 2px rgba(255,255,255,1)',
          }}>
            夜勤を辞めても、<span style={{ color: '#c8005a' }}>給料は下がらない。</span><br />知ってましたか？
          </h1>
          <p style={{
            fontSize: '14px',
            lineHeight: '1.85',
            color: '#2e1a35',
            marginBottom: '30px',
            fontWeight: '600',
            textShadow: '0 1px 6px rgba(255,255,255,0.85)',
          }}>
            美容クリニック専門の求人マッチングで、<br />
            日勤のみ・月収35万以上の「隠れた好条件」をご紹介します。
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
            <button
              style={{ ...ctaBtnBase, padding: '16px 0', fontSize: '16px', width: '100%', maxWidth: '340px', display: 'block', animation: 'shimmer 3s linear infinite' }}
              onClick={() => openForm('nijikango_tap_hero_cta')}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(255,105,180,0.40)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,105,180,0.30)'; }}
            >
              <span style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px', opacity: 0.9 }}>⏱ 30秒で完了</span>
              無料で相談する
            </button>
          </div>
          <p style={{ fontSize: '11px', color: '#3a1f40', fontWeight: '600', textShadow: '0 1px 4px rgba(255,255,255,0.8)' }}>※ 完全無料・秘密厳守・しつこい営業は一切ありません</p>
        </div>
      </section>

      {/* ===== JOBS CAROUSEL ===== */}
      <section style={{ padding: '56px 0', background: '#fefbfc' }}>
        <div style={{ padding: '0 20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#FF69B4', letterSpacing: '2px', marginBottom: '10px', textAlign: 'center' }}>JOBS</div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', textAlign: 'center', marginBottom: '6px', fontFamily: "'Zen Maru Gothic', sans-serif", lineHeight: '1.5' }}>
            厳選された美容クリニック求人
          </h2>
          <p style={{ fontSize: '11px', color: '#8a7a8e', textAlign: 'center', marginBottom: '24px' }}>
            ※ あくまでも一例です。
          </p>
        </div>

        <div
          style={{ width: '100%', overflow: 'hidden', position: 'relative' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={(e) => { handleTouchEnd(e); sendGA4Event('nijikango_swipe_jobs'); }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <div style={{
            display: 'flex',
            width: `calc(100vw * ${TOTAL})`,
            transform: `translateX(calc(-100vw * ${currentSlide}))`,
            transition: 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'grab',
            userSelect: 'none',
          }}>
            {shuffledJobs.map((job, index) => (
              <div key={index} style={{
                width: '100vw',
                flexShrink: 0,
                padding: '0 20px',
                boxSizing: 'border-box',
              }}>
                <JobCard job={job} />
              </div>
            ))}
          </div>
        </div>

        {/* ドットナビゲーション */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap', padding: '0 20px' }}>
          {shuffledJobs.map((_, index) => (
            <div
              key={index}
              onClick={() => { goToSlide(index); sendGA4Event('nijikango_tap_jobs_dot'); }}
              style={{
                width: currentSlide === index ? '22px' : '6px',
                height: '6px',
                borderRadius: '3px',
                background: currentSlide === index ? '#FF69B4' : '#FFB6C1',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* カルーセル下CTA */}
        <div style={{ textAlign: 'center', marginTop: '32px', padding: '0 20px' }}>
          <p style={{ fontSize: '14px', marginBottom: '18px', color: '#3a2d3d' }}>
            他にも<span style={hl}>200件以上</span>の美容クリニック求人をご用意しています。
          </p>
          <button
            style={{ ...ctaBtnBase, padding: '14px 36px', fontSize: '15px' }}
            onClick={() => openForm('nijikango_tap_jobs_cta')}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            無料で相談する
          </button>
        </div>
      </section>

      {/* ===== BENEFIT ===== */}
      <section style={{ padding: '56px 20px', background: 'linear-gradient(180deg, #FFF8FA 0%, #FEFBFC 100%)' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#FF69B4', letterSpacing: '2px', marginBottom: '10px', textAlign: 'center' }}>BENEFIT</div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', textAlign: 'center', marginBottom: '28px', fontFamily: "'Zen Maru Gothic', sans-serif", lineHeight: '1.5' }}>
          『日勤のみ』でも、あなたの価値は変わりません。
        </h2>
        <div style={{ display: 'grid', gap: '16px' }}>
          {[
            { icon: '💰', title: '収入を維持', desc: '美容クリニックは自由診療のため高単価。夜勤なしでも月収35万円以上の求人を多数ご紹介。', gaEvent: 'nijikango_tap_benefit_income' },
            { icon: '🕐', title: '定時退社', desc: '完全予約制のクリニックが多く、残業はほぼなし。プライベートの時間をしっかり確保できます。', gaEvent: 'nijikango_tap_benefit_time' },
            { icon: '🌸', title: '未経験OK', desc: '美容業界が初めてでも大丈夫。充実した研修制度と、専門アドバイザーが徹底サポート。', gaEvent: 'nijikango_tap_benefit_beginner' },
          ].map((item) => (
            <div key={item.title}
              onClick={() => sendGA4Event(item.gaEvent)}
              style={{ background: '#fff', padding: '22px', borderRadius: '16px', boxShadow: '0 4px 16px rgba(58,45,61,0.08)', textAlign: 'center', cursor: 'pointer' }}>
              <div style={{ fontSize: '38px', marginBottom: '10px' }}>{item.icon}</div>
              <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '8px', fontFamily: "'Zen Maru Gothic', sans-serif" }}>{item.title}</h3>
              <p style={{ fontSize: '13px', lineHeight: '1.8', color: '#6b5a6e' }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section style={{ padding: '56px 20px', background: 'linear-gradient(180deg, #FFF5F7 0%, #FEFBFC 100%)' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#FF69B4', letterSpacing: '2px', marginBottom: '10px', textAlign: 'center' }}>WHY US</div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', textAlign: 'center', marginBottom: '28px', fontFamily: "'Zen Maru Gothic', sans-serif", lineHeight: '1.5' }}>
          なぜ、私たちの診断で<br />理想の職場が見つかるのか？
        </h2>
        <div style={{ display: 'grid', gap: '20px' }}>
          {[
            { num: '01', title: '膨大な求人データ', desc: '40代・夜勤なし・月収35万円以上の『隠れた優良求人』を可視化。美容クリニックに特化しているからこそ、一般の求人サイトには掲載されない非公開求人も多数保有。', gaEvent: 'nijikango_tap_whyus_01' },
            { num: '02', title: 'プロによる診断', desc: '表面上の条件だけでなく、あなたのライフスタイルに最適な『負けない働き方』を導き出します。美容業界に精通した専門アドバイザーが、一人ひとり丁寧にヒアリング。', gaEvent: 'nijikango_tap_whyus_02' },
          ].map((item) => (
            <div key={item.num}
              onClick={() => sendGA4Event(item.gaEvent)}
              style={{ background: '#fff', padding: '22px', borderRadius: '16px', boxShadow: '0 4px 16px rgba(58,45,61,0.08)', display: 'flex', gap: '16px', alignItems: 'flex-start', cursor: 'pointer' }}>
              <div style={{ fontSize: '44px', fontWeight: '700', color: '#FFE4E1', fontFamily: "'Zen Maru Gothic', sans-serif", lineHeight: '1', flexShrink: 0 }}>{item.num}</div>
              <div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '10px', fontFamily: "'Zen Maru Gothic', sans-serif" }}>{item.title}</h3>
                <p style={{ fontSize: '13px', lineHeight: '1.8', color: '#6b5a6e' }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== FORM MODAL ===== */}
      {isFormOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', animation: 'fadeIn 0.25s ease-out' }}
          onClick={closeForm}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', maxWidth: '480px', width: '100%', maxHeight: '92vh', overflowY: 'auto', position: 'relative', animation: 'slideUp 0.3s ease-out' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '22px 22px 14px', borderBottom: '1px solid #FFE4E1', position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '20px 20px 0 0' }}>
              <button
                onClick={closeForm}
                style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', fontSize: '22px', color: '#8a7a8e', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
              >×</button>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#FF69B4' }}>⏱ 30秒で入力完了。まずはお気軽にご相談ください。</p>
            </div>
            <div style={{ padding: '22px' }}>
              {[
                { label: '保有資格', key: 'qualification', options: ['正看護師', '准看護師', '保健師', '助産師'] },
                { label: '希望の働き方', key: 'workStyle', options: ['常勤（夜勤NG）', '非常勤（夜勤NG）', 'こだわらない'] },
              ].map((field) => (
                <div key={field.key} style={{ marginBottom: '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px', fontSize: '13px', fontWeight: '600' }}>
                    <span>{field.label}</span>
                    <span style={{ background: '#FF69B4', color: '#fff', fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>必須</span>
                  </div>
                  <select
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #FFB6C1', fontSize: '14px', color: '#3a2d3d', background: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23FF69B4' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
                    value={(formData as Record<string, string>)[field.key]}
                    onChange={(e) => {
                      setFormData({ ...formData, [field.key]: e.target.value });
                      if (e.target.value) {
                        const eventMap: Record<string, string> = {
                          qualification: 'nijikango_form_sikaku',
                          workStyle: 'nijikango_form_workstyle',
                        };
                        sendGA4Event(eventMap[field.key]);
                      }
                    }}
                  >
                    <option value="">選択してください</option>
                    {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              ))}
              {[
                { label: '郵便番号', key: 'postalCode', type: 'text', placeholder: '1234567（ハイフンなし）', gaEvent: 'nijikango_form_postal_code' },
                { label: 'お名前', key: 'name', type: 'text', placeholder: '山田 花子', gaEvent: 'nijikango_form_name' },
                { label: '電話番号', key: 'phone', type: 'tel', placeholder: '09012345678', gaEvent: 'nijikango_form_phone' },
              ].map((field) => (
                <div key={field.key} style={{ marginBottom: '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px', fontSize: '13px', fontWeight: '600' }}>
                    <span>{field.label}</span>
                    <span style={{ background: '#FF69B4', color: '#fff', fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>必須</span>
                  </div>
                  <input
                    type={field.type}
                    placeholder={field.placeholder}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '2px solid #FFB6C1', fontSize: '14px', color: '#3a2d3d', background: '#fff' }}
                    value={(formData as Record<string, string>)[field.key]}
                    onChange={(e) => {
                      setFormData({ ...formData, [field.key]: e.target.value });
                    }}
                    onBlur={(e) => {
                      if (e.target.value) {
                        sendGA4Event(field.gaEvent);
                      }
                    }}
                  />
                </div>
              ))}
              <button
                style={{ ...ctaBtnBase, width: '100%', padding: '15px', fontSize: '16px', marginTop: '8px', animation: isSubmitting ? 'none' : 'pulse 2s ease-in-out infinite', background: isSubmitting ? '#ccc' : 'linear-gradient(135deg, #FF69B4 0%, #FFB6C1 100%)', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                <span style={{ fontSize: '11px', fontWeight: '400', display: 'block', marginBottom: '3px' }}>無料</span>
                {isSubmitting ? '送信中...' : '無料で相談する'}
              </button>
              <div style={{ fontSize: '11px', color: '#8a7a8e', marginTop: '14px', lineHeight: '1.7' }}>
                <p>※ 個人情報は厳重に管理し、第三者へ提供することはありません。</p>
                <p>※ 無理な勧誘は一切行いません。</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== FOOTER ===== */}
      <footer style={{ background: '#3a2d3d', padding: '32px 20px 28px', textAlign: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
          {[
            { label: '運営会社', href: 'https://nijikango.co.jp/' },
            { label: 'プライバシーポリシー', href: 'https://lp.nijikango.co.jp/privacy-policy' },
            { label: '利用規約', href: 'https://lp.nijikango.co.jp/terms-of-service' },
          ].map((link) => (
            <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer"
              onClick={() => sendGA4Event(`nijikango_tap_footer_${link.label}`)}
              style={{ color: '#FFB6C1', fontSize: '13px', textDecoration: 'none' }}>
              {link.label}
            </a>
          ))}
        </div>
        <p style={{ color: '#8a7a8e', fontSize: '12px' }}>© ニジ看護転職</p>
      </footer>
    </>
  );
}
